/**
 * The Calculator's four anchors — now a SHIM over the one registry in `domain/rulebook.ts`.
 *
 * This file used to be the second of two anchor registries: its own type, its own per-entry
 * `pending` flag, its own inert-mark component, and no cross-reference to the other one. That was
 * flagged when the Calculator shipped and deliberately deferred, because neither registry could
 * resolve anything and a duplicate that is dead in both halves costs nothing.
 *
 * League › Rules ended that. Both halves now open the same destination, so two registries would
 * mean two ways to be wrong about the same link — and the ids, the labels and the rule lists were
 * already duplicated by hand between them. The four entries moved into `RULE_ANCHORS`; what
 * remains here is the shape the Calculator's five call sites and its tests already import, built
 * from that table rather than restating it.
 *
 * `pending` is no longer a hand-set constant. It is `!ANCHORS_LIVE`, so this file cannot claim a
 * destination is unbuilt after the day it was built.
 */

import { ANCHORS_LIVE, RULE_ANCHORS, anchorHref as hrefFor } from './rulebook.js';

export interface RulebookAnchor {
  /** The `id` the Rulebook heading will carry, and the fragment the link points at. */
  id: string;
  /** The mark's accessible name — what a screen reader says instead of "section". */
  label: string;
  /** The rules this destination has to hold, oldest first. */
  rules: readonly string[];
  /** True while the destination is unbuilt. A pending anchor renders inert rather than broken. */
  pending: boolean;
}

const from = (key: string): RulebookAnchor => {
  const a = RULE_ANCHORS[key];
  if (!a) throw new Error(`rulebookAnchors: "${key}" is not in RULE_ANCHORS.`);
  return { id: a.id, label: a.label, rules: a.rules.map((r) => r.id), pending: !ANCHORS_LIVE };
};

export const CALCULATOR_ANCHORS = {
  keeperCost: from('keeper-cost-and-the-grid'),
  sleeperReward: from('sleeper-reward'),
  luxuryTax: from('luxury-tax'),
  tradeNoReset: from('trade-does-not-reset-keeper-standing'),
} as const satisfies Record<string, RulebookAnchor>;

/** Every anchor the app names, for the test that checks the ids are unique and URL-safe. */
export const ALL_ANCHORS: readonly RulebookAnchor[] = Object.values(CALCULATOR_ANCHORS);

/**
 * `#/league/rules/2017-II-1` — the route, not a bare fragment.
 *
 * It used to be `#${a.id}`, a fragment for a heading on a page that did not exist. The page that
 * exists routes by hash, so the destination is a real location: section, page, and the rule to
 * open. Anything that cannot resolve still returns a fragment-shaped string rather than null, so a
 * caller rendering an `href` never gets `undefined` in the DOM.
 */
export const anchorHref = (a: RulebookAnchor): string => hrefFor(a.id) ?? `#${a.id}`;
